// client/src/components/Receipts/parts/ReceiptLinkSuggestions.jsx
// AI-1: Smart Auto-Linking drawer — matches orphan receipts to transactions
import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, Stack, Button, CircularProgress,
  Card, CardContent, Divider, Chip, IconButton, Alert
} from '@mui/material';
import { Close as CloseIcon, Link as LinkIcon, Lock as LockIcon } from '@mui/icons-material';
import api from '../../../services/api';
import { formatCurrency, formatDate } from '../../../utils/helpers';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useUpgradeModal } from '../../../contexts/UpgradeModalContext';
import PremiumBadge from '../../Subscription/PremiumBadge';

const ReceiptLinkSuggestions = ({ open, onClose, receipt, onLinked }) => {
  const { hasFeature, subscription } = useSubscription();
  const { open: openUpgrade } = useUpgradeModal();
  const isUnlocked = hasFeature('receiptAI');

  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [linking, setLinking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !receipt?.id) return;
    if (!isUnlocked) return; // skip API call for locked users
    setLoading(true);
    setError(null);
    setCandidates([]);
    api.post(`/receipts/${receipt.id}/suggest-links`)
      .then(res => setCandidates(res.data.candidates || []))
      .catch(() => setError('Could not load suggestions'))
      .finally(() => setLoading(false));
  }, [open, receipt?.id, isUnlocked]);

  const handleLink = async (txId) => {
    setLinking(txId);
    try {
      await api.patch(`/receipts/${receipt.id}/link`, { transactionId: txId });
      // AI-7: fetch suggested category after link
      let suggestedCategory = null;
      try {
        const catRes = await api.get(`/receipts/${receipt.id}/suggested-category`);
        if (catRes.data.category) suggestedCategory = catRes.data.category;
      } catch {
        // silent — category suggestion is best-effort
      }
      onLinked(receipt.id, txId, suggestedCategory);
      onClose();
    } catch {
      setError('Failed to link. Please try again.');
    } finally {
      setLinking(null);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '70vh' } }}
    >
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">🔗 Link to Transaction</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {!isUnlocked ? (
          // Locked empty state for Free users — friendly upgrade CTA
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <LockIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6, mb: 1 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              AI-powered smart linking
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Premium scans your transactions and suggests the best matches for{' '}
              <strong>{receipt?.merchantName}</strong>
              {receipt?.totalAmount != null && <> · {formatCurrency(receipt?.totalAmount)}</>}
              .
            </Typography>
            <Box sx={{ display: 'inline-block', mb: 2 }}>
              <PremiumBadge tier="premium" size="small" />
            </Box>
            <Box>
              <Button
                variant="contained"
                color="warning"
                startIcon={<LockIcon />}
                onClick={() => {
                  openUpgrade({
                    feature: 'receiptAI',
                    requiredPlan: 'premium',
                    promoText: 'AI smart linking',
                    currentPlan: subscription?.plan || 'free',
                    source: 'user',
                  });
                }}
                sx={{ fontWeight: 700, textTransform: 'none' }}
              >
                Unlock with Premium
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Button fullWidth onClick={onClose} color="inherit">Close</Button>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing transactions that match <strong>{receipt?.merchantName}</strong> · {formatCurrency(receipt?.totalAmount)}
            </Typography>

            {loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && candidates.length === 0 && !error && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No matching transactions found</Typography>
                <Typography variant="caption" color="text.secondary">
                  Try adjusting the date range on the transaction
                </Typography>
              </Box>
            )}

            <Stack spacing={1.5}>
              {candidates.map((c) => (
                <Card
                  key={c.txId}
                  variant="outlined"
                  sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{c.description}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(c.date)}</Typography>
                      </Box>
                      <Typography variant="body1" fontWeight={700}>{formatCurrency(c.amount)}</Typography>
                      <Chip
                        size="small"
                        label={`${c.score}%`}
                        color={c.score > 80 ? 'success' : c.score > 60 ? 'warning' : 'default'}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={linking === c.txId ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                        disabled={!!linking}
                        onClick={() => handleLink(c.txId)}
                      >
                        Link
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Button fullWidth onClick={onClose} color="inherit">Skip for now</Button>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ReceiptLinkSuggestions;
